<template>
<!-- Hero Section -->
    <section id="home" class="hero">
        <div class="container">
            <div class="hero-content">
                <div class="hero-text">
                    <h1>SivaJyothirmayi P</h1>
                    <p class="hero-subtitle">Technical Leader | Agile Strategist | Team Enabler</p>
                    <p class="hero-tagline">Empowering Teams to Transform Vision into Reality</p>
                    <a href="#contact" class="cta-button">Let's Connect</a>
                </div>
                <div class="hero-accent">
                    Great outcomes emerge when talented people are given the right environment, strategic guidance, and freedom to innovate.
                </div>
            </div>
        </div>
    </section>

    <!-- About Section -->
    <section id="about">
        <div class="container">
            <div class="section-header">
                <h2>About Me</h2>
            </div>
            <div class="about-content">
                <p>With over 16 years of experience leading technical teams and enabling startups to achieve their ambitious goals, I've made it my mission to <span class="highlight">transform potential into performance</span>.</p>
                
                <p>My journey spans leadership roles at Cisco, TCS, HCL Technologies, and Wipro, where I've led high-performing teams through complex technical challenges. Currently serving as a Technical Project Manager at Persistent Systems, I create environments where teams don't just meet expectations—they exceed them.</p>
                
                <p>My leadership philosophy is simple yet powerful: <span class="highlight">trust over micromanagement, clarity over confusion, and empowerment over control</span>. I help teams discover their strengths, maintain the discipline needed for focus, and deliver results that matter.</p>
                
                <p>Beyond corporate leadership, I work with emerging startups and ventures, helping founders and teams navigate the journey from vision to market reality—whether it's refining product strategy, building effective development processes, or creating scalable quality frameworks.</p>
            </div>
        </div>
    </section>

    <!-- What I Do Section -->
    <section id="services">
        <div class="container">
            <div class="section-header">
                <h2>How I Enable Success</h2>
            </div>
            <div class="services-grid">
                <div class="service-card">
                    <h3>Team Leadership & Empowerment</h3>
                    <p>I create high-trust environments where teams thrive. Rather than micromanaging, I establish clear goals, remove obstacles, and empower individuals to take ownership. The result? Teams that are engaged, innovative, and consistently deliver exceptional outcomes.</p>
                </div>
                <div class="service-card">
                    <h3>Startup Advisory & Guidance</h3>
                    <p>
                        I work with emerging ventures to transform ambitious ideas into successful products. From product strategy and development roadmaps to quality assurance and go-to-market planning, I provide hands-on guidance that helps startups navigate challenges and accelerate growth.
                        <br/> Currently helping &nbsp;<a href="https://mviaso.com/" target="_blank">Mviaso</a>
                        &nbsp;<a href="https://pabu.ai/" target="_blank">Pabu</a>
                        &nbsp;<a href="https://qayilabs.com/" target="_blank">QayiLabs</a>
                    </p>
                </div>
                <div class="service-card">
                    <h3>Agile Transformation</h3>
                    <p>I help organizations and teams adopt Agile methodologies that actually work—not just ceremonies, but cultural shifts that improve collaboration, speed up delivery, and enhance quality. My approach balances flexibility with the discipline needed to maintain focus and meet commitments.</p>
                </div>
                <div class="service-card">
                    <h3>Technical Strategy</h3>
                    <p>With expertise spanning Core Java, Microservices, Docker, Kubernetes, and enterprise systems, I bridge the gap between technical execution and business objectives. I help teams make smart architectural decisions that enable scalability and long-term success.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Experience Section -->
    <section id="experience">
        <div class="container">
            <div class="section-header">
                <h2>Professional Journey</h2>
            </div>
            <div class="timeline">
                <div class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-company">Persistent Systems</div>
                        <div class="timeline-role">Technical Project Manager</div>
                        <div class="timeline-period">Feb 2025 - Present | Bengaluru, India</div>
                        <div class="timeline-description">Leading cross-functional teams to deliver complex technical solutions while fostering collaboration and innovation. Driving Agile transformation initiatives that empower teams to achieve excellence.</div>
                    </div>
                </div>
                <div class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-company">Persistent Systems</div>
                        <div class="timeline-role">Senior Architect</div>
                        <div class="timeline-period">May 2024 - Feb 2025 | Bengaluru, India</div>
                        <div class="timeline-description">Provided technical leadership and strategic guidance across multiple product initiatives, enabling teams to build scalable, maintainable solutions.</div>
                    </div>
                </div>
                <div class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-company">Cisco Systems</div>
                        <div class="timeline-role">Senior Software Engineer & Team Lead</div>
                        <div class="timeline-period">Apr 2015 - May 2024 (9+ years) | Bengaluru, India</div>
                        <div class="timeline-description">Led agile teams in developing network management applications for Optical Transport Networks, Element Management Systems, and Performance Management solutions. Recognized for building high-performing teams and delivering critical customer solutions with excellence.</div>
                    </div>
                </div>
                <div class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-company">Tata Consultancy Services</div>
                        <div class="timeline-role">Team Lead</div>
                        <div class="timeline-period">Nov 2014 - Mar 2015 | Bengaluru, India</div>
                        <div class="timeline-description">Led technical teams in successful enterprise software delivery, focusing on team development and project execution excellence.</div>
                    </div>
                </div>
                <div class="timeline-item">
                    <div class="timeline-dot"></div>
                    <div class="timeline-content">
                        <div class="timeline-company">HCL Technologies & Wipro</div>
                        <div class="timeline-role">Senior Software Engineer</div>
                        <div class="timeline-period">2007 - 2014 (7+ years combined) | Bengaluru, India</div>
                        <div class="timeline-description">Built strong technical foundation through hands-on development and team collaboration across enterprise applications.</div>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <!-- Strengths Section -->
    <section id="strengths">
        <div class="container">
            <div class="section-header">
                <h2>Core Strengths</h2>
            </div>
            <div class="strengths-grid">
                <div class="strength-card">
                    <div class="strength-icon">🎯</div>
                    <h3>Empowering Leadership</h3>
                    <p>Creating environments where teams discover their potential, take ownership, and consistently exceed expectations through trust and strategic guidance.</p>
                </div>
                <div class="strength-card">
                    <div class="strength-icon">🚀</div>
                    <h3>Startup Enablement</h3>
                    <p>Helping founders and early-stage ventures navigate product development, quality frameworks, team building, and go-to-market strategies.</p>
                </div>
                <div class="strength-card">
                    <div class="strength-icon">⚡</div>
                    <h3>Agile Excellence</h3>
                    <p>Implementing Agile methodologies that balance speed with discipline, fostering collaboration while maintaining focus on delivery commitments.</p>
                </div>
                <div class="strength-card">
                    <div class="strength-icon">💻</div>
                    <h3>Technical Depth</h3>
                    <p>Deep expertise in Java ecosystems, Microservices architecture, Cloud technologies, and Telecom Network Management Systems.</p>
                </div>
                <div class="strength-card">
                    <div class="strength-icon">🎨</div>
                    <h3>Strategic Thinking</h3>
                    <p>Bridging technical execution with business objectives, making decisions that drive both immediate results and long-term success.</p>
                </div>
                <div class="strength-card">
                    <div class="strength-icon">💬</div>
                    <h3>Effective Communication</h3>
                    <p>Building alignment across diverse stakeholders—from engineering teams to executive leadership—ensuring everyone moves forward together.</p>
                </div>
            </div>
        </div>
    </section>

    <!-- Philosophy Section -->
    <section id="philosophy">
        <div class="container">
            <div class="section-header">
                <h2>Leadership Philosophy</h2>
            </div>
            <div class="philosophy-content">
                <p>Great outcomes don't come from micromanagement—they emerge when talented people are trusted, empowered, and given the clarity they need to succeed.</p>
                
                <p>I believe in creating environments where:</p>
                <ul class="philosophy-list">
                    <li><strong>Trust</strong> replaces surveillance</li>
                    <li><strong>Clarity</strong> eliminates confusion</li>
                    <li><strong>Empowerment</strong> drives ownership</li>
                    <li><strong>Discipline</strong> maintains focus without stifling creativity</li>
                    <li><strong>Collaboration</strong> amplifies individual strengths</li>
                </ul>
                
                <p>Whether leading corporate teams or advising startups, my approach remains consistent: understand the vision, remove the obstacles, provide strategic guidance, and let talented people do what they do best.</p>
                
                <p>Success isn't measured just by what gets delivered—it's measured by how teams grow, how individuals develop, and how sustainable the results are for the long term.</p>
            </div>
        </div>
    </section>

    <!-- Education & Certifications -->
    <section id="education">
        <div class="container">
            <div class="section-header">
                <h2>Education & Professional Development</h2>
            </div>
            <div class="edu-cert-grid">
                <div class="edu-section">
                    <h3 style="margin-bottom: 2rem; color: var(--primary-dark);">Education</h3>
                    <div class="edu-item">
                        <div class="edu-degree">Master of Science (MS)</div>
                        <div class="edu-institution">Birla Institute of Technology and Science, Pilani</div>
                        <div class="edu-period">Computer Science | 2007 - 2011</div>
                    </div>
                    <div class="edu-item">
                        <div class="edu-degree">Bachelor of Science (BS)</div>
                        <div class="edu-institution">Mount Carmel College, Bangalore</div>
                        <div class="edu-period">Mathematics and Computer Science | 2005 - 2007</div>
                    </div>
                </div>
                <div class="cert-section">
                    <h3 style="margin-bottom: 2rem; color: var(--primary-dark);">Recent Certifications</h3>
                    <ul class="cert-list">
                        <li>
                            <div class="cert-name">Growth Mindset: Key to Confidence & Impact</div>
                            <div class="cert-issuer">NASBA (2025)</div>
                        </li>
                        <li>
                            <div class="cert-name">Critical Thinking for Better Decisions</div>
                            <div class="cert-issuer">NASBA (2025)</div>
                        </li>
                        <li>
                            <div class="cert-name">Women in Leadership</div>
                            <div class="cert-issuer">Udemy (2025)</div>
                        </li>
                        <li>
                            <div class="cert-name">Generative AI for Project Managers</div>
                            <div class="cert-issuer">PMI (2024)</div>
                        </li>
                        <li>
                            <div class="cert-name">Lean Six Sigma Foundations</div>
                            <div class="cert-issuer">LinkedIn (2024)</div>
                        </li>
                        <li>
                            <div class="cert-name">Program Management & Team Leadership</div>
                            <div class="cert-issuer">LinkedIn (2024)</div>
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </section>

    <!-- Contact Section -->
    <section id="contact">
        <div class="container">
            <div class="section-header">
                <h2>Let's Work Together</h2>
            </div>
            <div class="contact-content">
                <p>Whether you're building a startup, leading a technical team, or transforming your organization's delivery capabilities, I'd love to explore how we can work together to turn your vision into reality.</p>
                <div class="contact-info">
                    <div class="contact-item">
                        <span>📍</span>
                        <span>Bengaluru, Karnataka, India</span>
                    </div>
                    <div class="contact-item">
                        <span>💼</span>
                        <a href="https://www.linkedin.com/in/sivajyothi/" target="_blank">Connect on LinkedIn</a>
                    </div>
                    <div class="contact-item">
                        <span>✉️</span>
                        <a href="mailto:sivajyothirmayi.p@gmail.com">sivajyothirmayi.p@gmail.com</a>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
export default {
  name: "HeroSection",
};
</script>

<style scoped>

</style>
